'use client';

import HoverPrefetchLink from '@/components/hover-prefetch-link';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { websiteEnv } from '@/constants/website-env';
import { useAppContext } from '@/context/app';
import { generateUrl } from '@/util/imageUtils';
import clsx from 'clsx';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { Fragment, ReactNode, useMemo, useState } from 'react';
import Price from '../Price';

type ColorOption = NonNullable<
  NonNullable<NonNullable<NonNullable<GetColorMenusQuery['bridesmaids']>['aggregations']>[0]>['options']
>[0];

interface Props {
  colorMenus: Record<
    'bridesmaids' | 'guest' | 'moms' | 'dresses' | 'prom' | 'homecoming' | 'graduation',
    ColorOption[]
  >;
}

const NavMenu = (props: Props) => {
  const { colorMenus } = props;
  const [{ activeColumn, cartActiveColumn }] = useAppContext();
  const pathname = usePathname();

  const isAttire = (pathname === '/cart' ? cartActiveColumn : activeColumn) === 'attire';

  const menus = useMemo<{ label: ReactNode; href: string; dropdown: ReactNode; visible: boolean }[]>(() => {
    const bridesmaidColorMenu = colorMenus?.bridesmaids?.length
      ? generateColorMenuNode(
          colorMenus.bridesmaids,
          websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
          '/bridesmaid-dresses',
        )
      : null;
    const guestColorMenu = colorMenus?.guest?.length
      ? generateColorMenuNode(
          colorMenus.guest,
          websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
          '/wedding-guest-dresses',
        )
      : null;
    const momsColorMenu = colorMenus?.moms.length
      ? generateColorMenuNode(
          colorMenus.moms,
          websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
          '/mother-of-the-bride-dresses',
        )
      : null;
    const dressesColorMenu = colorMenus?.dresses.length
      ? generateColorMenuNode(
          colorMenus.dresses,
          websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
          '/dresses',
        )
      : null;
    const promDressesColorMenu = colorMenus?.prom.length
      ? generateColorMenuNode(
          colorMenus.prom,
          websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
          '/prom-dresses',
        )
      : null;
    const graduationDressesColorMenu = colorMenus?.graduation.length
      ? generateColorMenuNode(
          colorMenus.graduation,
          websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
          '/graduation-dresses',
        )
      : null;
    const homecomingDressesColorMenu = colorMenus?.homecoming.length
      ? generateColorMenuNode(
          colorMenus.homecoming,
          websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
          '/homecoming-dresses',
        )
      : null;

    return [
      {
        visible: isAttire,
        label: 'OCCASION',
        href: '/dresses',
        dropdown: (
          <DropdownWrapper>
            <div className={clsx('flex items-start justify-between gap-x-10')}>
              <ul className={clsx('flex  items-start justify-start ', 'gap-x-[100px]')}>
                {[
                  {
                    label: 'OCCASION DRESSES',
                    children: [
                      {
                        label: 'All Occassion Dresses',
                        href: '/dresses',
                      },
                      {
                        label: 'Holiday Dresses',
                        href: '/holiday-dresses',
                      },
                      {
                        label: 'Party Dresses',
                        href: '/party-dresses',
                      },
                      {
                        label: 'Evening Dresses',
                        href: '/evening-dresses',
                      },
                      {
                        label: 'Formal Dresses',
                        href: '/formal-dresses',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY DESIGN',
                    children: [
                      {
                        label: 'Sequin Dresses',
                        href: '/dresses/fabric/sequin',
                      },
                      {
                        label: 'Velvet Dresses',
                        href: '/dresses/fabric/velvet',
                      },
                      {
                        label: 'Satin Dresses',
                        href: '/dresses/fabric/stretch-satin',
                      },
                      {
                        label: 'Lace Dresses',
                        href: '/dresses/fabric/lace',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY COLOR',
                    children: [
                      {
                        label: 'Red Dresses',
                        href: `/dresses/${COLORS_FAMILY_KEY}/red `,
                      },
                      {
                        label: 'Green Dresses',
                        href: `/dresses/${COLORS_FAMILY_KEY}/green`,
                      },
                      {
                        label: 'Black Dresses',
                        href: `/dresses/${COLORS_FAMILY_KEY}/black`,
                      },
                      {
                        label: 'Gold Dresses',
                        href: '/dresses/color/light-gold',
                      },
                      {
                        label: 'Blue Dresses',
                        href: `/dresses/${COLORS_FAMILY_KEY}/blue`,
                      },
                    ],
                  },
                ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                  acc.push(
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>,
                  );

                  return acc;
                }, [])}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/occasion-new-arrivals.webp`,
                    label: 'NEW ARRIVALS',
                    href: '/dresses?sort_by=new',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/occasion-satin.webp`,
                    label: 'SATIN',
                    href: `/dresses/fabric/stretch-satin`,
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={1022}
                          height={1500}
                          alt={label}
                          className='h-auto w-[220px]'
                          unoptimized
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        visible: isAttire,
        label: 'PROM',
        href: '/prom-dresses',
        dropdown: (
          <DropdownWrapper>
            <div className={clsx('flex items-start justify-between gap-x-10')}>
              <ul className={clsx('flex  items-start justify-start ', 'gap-x-[100px]')}>
                {[
                  {
                    label: 'PROM DRESSES',
                    children: [
                      {
                        label: 'All Prom Dresses',
                        href: '/prom-dresses',
                      },
                      {
                        label: (
                          <span>
                            <span>Under</span>
                            &nbsp;
                            <Price
                              value={100}
                              showFractionDigits={false}
                              currencyCode={process.env.NEXT_PUBLIC_DEFAULT_DISPLAY_CURRENCY_CODE}
                            />
                          </span>
                        ),
                        href: '/prom-dresses/price/0-100',
                      },
                      {
                        label: 'Long',
                        href: '/long-prom-dresses',
                      },
                      {
                        label: 'Plus Size',
                        href: '/plus-size-prom-dresses',
                        visible: false,
                      },
                      {
                        label: 'Gold',
                        href: '/prom-dresses/color/light-gold',
                      },
                      {
                        label: 'Floral',
                        href: '/floral-dresses',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY TRENDS',
                    children: [
                      {
                        label: 'A-Line',
                        href: '/prom-dresses/silhouette/a-line',
                      },
                      {
                        label: 'Ball Gown',
                        href: '/prom-dresses/silhouette/ball-gown',
                        visible: false,
                      },
                      {
                        label: 'Mermaid',
                        href: '/prom-dresses/silhouette/mermaid',
                      },
                      {
                        label: 'Sequins',
                        href: '/prom-dresses/embellishment/sequin',
                        visible: false,
                      },
                      {
                        label: 'Corset',
                        href: '/prom-dresses/embellishment/corset',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY FABRIC',
                    children: [
                      {
                        label: 'Satin',
                        href: '/prom-dresses/fabric/satin-charmeuse',
                      },
                      {
                        label: 'Lace',
                        href: '/prom-dresses/fabric/lace',
                      },
                      {
                        label: 'Tulle',
                        href: '/prom-dresses/fabric/tulle',
                      },
                    ],
                  },
                ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                  acc.push(
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href, visible = true }, i) => {
                          if (!visible) return null;

                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>,
                  );

                  if (i === 1 && promDressesColorMenu) {
                    acc.push(<Fragment key='prom-dresses'>{promDressesColorMenu}</Fragment>);
                  }

                  return acc;
                }, [])}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/prom-new-arrivals.png`,
                    label: 'NEW ARRIVALS',
                    href: '/prom-dresses?sort_by=new',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/prom-floral-prom-dresses.png`,
                    label: 'FLORAL PROM DRESSES',
                    href: `/prom-dresses/${COLORS_FAMILY_KEY}/floral`,
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={220}
                          height={320}
                          alt={label}
                          quality={100}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        visible: isAttire,
        label: 'HOMECOMING',
        href: '/homecoming-dresses',
        dropdown: (
          <DropdownWrapper>
            <div className={clsx('flex items-start justify-between gap-x-10')}>
              <ul className={clsx('flex  items-start justify-start ', 'gap-x-[100px]')}>
                {[
                  {
                    label: 'HOMECOMING DRESSES',
                    children: [
                      {
                        label: 'All Homecoming Dresses',
                        href: '/homecoming-dresses',
                      },
                      {
                        label: 'Short',
                        href: '/short-homecoming-dresses',
                      },
                      {
                        label: 'Tight',
                        href: '/tight-homecoming-dresses',
                      },
                      {
                        label: 'Plus Size',
                        href: '/plus-size-homecoming-dresses',
                        visible: false,
                      },
                      {
                        label: 'Sequins',
                        href: '/homecoming-dresses/fabric/sequin',
                      },
                      {
                        label: 'Tiered',
                        href: '/homecoming-dresses/embellishment/tiers',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY FABRIC',
                    children: [
                      {
                        label: 'Satin',
                        href: '/homecoming-dresses/fabric/satin-charmeuse',
                        visible: false,
                      },
                      {
                        label: 'Lace',
                        href: '/homecoming-dresses/fabric/lace',
                        visible: false,
                      },
                      {
                        label: 'Tulle',
                        href: '/homecoming-dresses/fabric/tulle',
                      },
                    ],
                  },
                ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                  acc.push(
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href, visible = true }, i) => {
                          if (!visible) return null;

                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>,
                  );

                  if (i === 0 && homecomingDressesColorMenu) {
                    acc.push(<Fragment key='homecoming-dresses'>{homecomingDressesColorMenu}</Fragment>);
                  }

                  return acc;
                }, [])}
              </ul>

              <div className=' flex-none'>
                <HoverPrefetchLink
                  href='/short-homecoming-dresses'
                  className='peer block'
                >
                  <Image
                    src={process.env.NEXT_PUBLIC_BASE_PATH + `/images/home/homecoming-short-homecoming-dresses.png`}
                    width={600}
                    height={320}
                    alt='Short Homecoming Dresses'
                    quality={100}
                  />
                </HoverPrefetchLink>
                <HoverPrefetchLink
                  href='/short-homecoming-dresses'
                  className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                >
                  SHORT HOMECOMING DRESSES
                </HoverPrefetchLink>
              </div>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        visible: isAttire,
        label: 'GRADUATION',
        href: '/graduation-dresses',
        dropdown: (
          <DropdownWrapper>
            <div className={clsx('flex items-start justify-between gap-x-10')}>
              <ul className={clsx('flex  items-start justify-start ', 'gap-x-[100px]')}>
                {[
                  {
                    label: 'GRADUATION DRESSES',
                    children: [
                      {
                        label: 'All Graduation Dresses',
                        href: '/graduation-dresses',
                      },
                      {
                        label: (
                          <span>
                            <span>Under</span>
                            &nbsp;
                            <Price
                              value={100}
                              showFractionDigits={false}
                              currencyCode={process.env.NEXT_PUBLIC_DEFAULT_DISPLAY_CURRENCY_CODE}
                            />
                          </span>
                        ),
                        href: '/graduation-dresses/price/0-100',
                      },
                      {
                        label: 'Short',
                        href: '/short-graduation-dresses',
                      },
                      {
                        label: 'Long',
                        href: '/long-graduation-dresses',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY STYLE',
                    children: [
                      {
                        label: 'A-Line',
                        href: '/graduation-dresses/silhouette/a-line',
                      },
                      {
                        label: 'Mermaid',
                        href: '/graduation-dresses/silhouette/mermaid',
                      },
                      {
                        label: 'Sheath',
                        href: '/graduation-dresses/silhouette/sheath',
                        visible: false,
                      },
                    ],
                  },
                ].reduce<ReactNode[]>((acc, { label, children }, i) => {
                  acc.push(
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href, visible = true }, i) => {
                          if (!visible) return null;

                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>,
                  );

                  if (i === 1 && graduationDressesColorMenu) {
                    acc.push(<Fragment key='graduation-dresses'>{graduationDressesColorMenu}</Fragment>);
                  }

                  return acc;
                }, [])}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/graduation-new-arrivals.png`,
                    label: 'NEW ARRIVALS',
                    href: '/graduation-dresses?sort_by=new',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/graduation-long-graduation-dresses.png`,
                    label: 'LONG GRADUATION DRESSES',
                    href: '/long-graduation-dresses',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/graduation-white-graduation-dresses.png`,
                    label: 'WHITE GRADUATION DRESSES',
                    href: `/graduation-dresses/${COLORS_FAMILY_KEY}/white`,
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={220}
                          height={320}
                          alt={label}
                          quality={100}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        visible: !isAttire,
        label: (
          <div className='relative'>
            <span>BRIDESMAIDS</span>
            <div className='absolute left-1/2 top-0 block min-w-max -translate-x-1/2 -translate-y-full scale-[.8] rounded-[50px] bg-E7DED5 px-4 py-0.5 font-CeraPro text-[10px] leading-[17px] text-80583F'>
              {websiteEnv.isUnitedKingdom ? 'FREE CUSTOMISATION' : 'FREE CUSTOMIZATION'}
            </div>
          </div>
        ),
        href: '/bridesmaid-dresses',
        dropdown: (
          <DropdownWrapper>
            <div className={clsx('flex items-start justify-between gap-x-[100px]')}>
              <ul
                className={clsx(
                  'flex  items-start justify-start ',
                  websiteEnv.isUnitedStates ? 'gap-x-10' : 'gap-x-[71px]',
                )}
              >
                {[
                  {
                    label: 'BRIDESMAID DRESSES',
                    children: [
                      {
                        label: 'All Bridesmaid Dresses',
                        href: '/bridesmaid-dresses',
                      },
                      {
                        label: 'Long',
                        href: '/long-bridesmaid-dresses',
                      },
                      {
                        label: 'Short',
                        href: '/short-bridesmaid-dresses',
                      },
                      websiteEnv.isNewZealand || websiteEnv.isAustralia
                        ? null
                        : {
                            label: 'Velvet',
                            href: '/bridesmaid-dresses/fabric/velvet',
                          },
                      {
                        label: 'Floral',
                        href: '/floral-bridesmaid-dresses',
                      },
                      {
                        label: 'Convertible',
                        href: '/convertible-bridesmaid-dresses',
                      },
                      {
                        label: 'Jumpsuits',
                        href: '/bridesmaid-dresses/silhouette/jumpsuit',
                      },
                      {
                        label: 'Plus Size',
                        href: '/plus-size-dresses',
                      },
                      {
                        label: 'Maternity',
                        href: '/maternity-bridesmaid-dresses',
                      },
                      {
                        label: 'Junior',
                        href: '/junior-bridesmaid-dresses',
                      },
                      websiteEnv.isUnitedStates
                        ? {
                            label: 'Ready To Ship',
                            href: '/ready-to-ship',
                          }
                        : null,
                      {
                        label: '(Free!) Swatches',
                        href: '/swatches',
                      },
                    ].filter(x => !!x),
                  },
                  {
                    label: 'SHOP BY FABRIC',
                    children: [
                      {
                        label: 'All Fabric',
                        href: '/shop-by-fabric',
                      },
                      {
                        label: 'Satin Charmeuse',
                        href: '/bridesmaid-dresses/fabric/satin-charmeuse',
                      },
                      {
                        label: 'Crystal Satin',
                        href: '/bridesmaid-dresses/fabric/crystal-satin',
                      },
                      {
                        label: 'Chiffon',
                        href: '/bridesmaid-dresses/fabric/chiffon',
                      },
                      {
                        label: 'Velvet',
                        href: '/bridesmaid-dresses/fabric/velvet',
                      },
                      {
                        label: 'Stretch knitted fabric',
                        href: '/bridesmaid-dresses/fabric/stretch-knitted-fabric',
                      },
                      {
                        label: 'Lace',
                        href: `/bridesmaid-dresses/fabric/lace`,
                      },
                    ],
                  },
                  websiteEnv.isUnitedStates
                    ? {
                        label: 'ACCESSORIES',
                        children: [
                          {
                            label: 'Jackets & Wraps',
                            href: '/wedding-jackets-wraps',
                          },
                          {
                            label: 'Shoes',
                            href: '/wedding-shoes',
                          },
                          {
                            label: 'Bridesmaid Robes',
                            href: '/bridesmaid-robes',
                          },
                          {
                            label: 'Groomsmen',
                            href: '/groom-gifts',
                          },
                        ],
                      }
                    : null,
                  {
                    label: 'EXPLORE',
                    children: [
                      {
                        label: 'Free Moodboard',
                        href: '/free-moodboard',
                      },
                      {
                        label: 'Real Wedding',
                        href: '/real-wedding',
                      },
                      {
                        label: 'Size Guide',
                        href: '/size-chart',
                      },
                      {
                        label: `Fall Bridesmaid Dress ${websiteEnv.isUnitedKingdom ? 'Colour' : 'Color'} Palette`,
                        href: '/fall-bridesmaid-dresses',
                      },
                    ],
                  },
                ]
                  .filter(x => !!x)
                  .reduce<ReactNode[]>((acc, { label, children }, i, arr) => {
                    acc.push(
                      <li
                        key={i}
                        className='flex-none'
                      >
                        <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                        <ul className='mt-5 flex flex-col gap-y-1.5'>
                          {children.map(({ label, href }, i) => {
                            return (
                              <li key={i}>
                                <HoverPrefetchLink
                                  href={href}
                                  className='text-[15px] leading-[21px] lg_hover_underline'
                                >
                                  {label}
                                </HoverPrefetchLink>
                              </li>
                            );
                          })}
                        </ul>
                      </li>,
                    );

                    if (i === 0 && bridesmaidColorMenu) {
                      acc.push(<Fragment key='bridesmaid-dresses'>{bridesmaidColorMenu}</Fragment>);
                    }

                    return acc;
                  }, [])}
              </ul>

              <div className=' flex-none'>
                <HoverPrefetchLink
                  href='/bridesmaid-dresses?sort_by=best_sellers'
                  className='peer block'
                >
                  <Image
                    src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/bridesmaids-best-seller.png`}
                    width={463}
                    height={320}
                    alt='best-seller'
                    quality={90}
                  />
                </HoverPrefetchLink>
                <HoverPrefetchLink
                  href='/bridesmaid-dresses?sort_by=best_sellers'
                  className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                >
                  BEST SELLER
                </HoverPrefetchLink>
              </div>
            </div>
          </DropdownWrapper>
        ),
      },

      {
        visible: !isAttire,
        label: 'BRIDES',
        href: '/wedding-dresses',
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex items-start justify-start gap-x-[100px]'>
                {[
                  {
                    label: 'WEDDING DRESSES',
                    children: [
                      {
                        label: 'All Wedding Dresses',
                        href: '/wedding-dresses',
                      },
                      {
                        label: 'New Arrivals',
                        href: '/wedding-dresses?sort_by=new',
                      },
                      {
                        label: 'Plus Size',
                        href: '/plus-size-wedding-dresses',
                      },
                      {
                        label: 'Little White Dresses',
                        href: '/little-white-dresses',
                      },
                      {
                        label: 'Black Wedding Dresses',
                        href: '/wedding-dresses/color/black',
                      },
                      {
                        label: 'Floral Wedding Dresses',
                        href: '/floral-wedding-dresses',
                      },
                      {
                        label: 'Convertible Wedding Dresses',
                        href: '/convertible-wedding-dresses',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY STYLE',
                    children: [
                      {
                        label: 'Boho',
                        href: '/boho-wedding-dresses',
                      },
                      {
                        label: 'Lace',
                        href: `/wedding-dresses/fabric/lace`,
                      },
                      {
                        label: 'Beach',
                        href: '/beach-wedding-dresses',
                      },
                      {
                        label: 'Long Sleeves',
                        href: '/wedding-dresses/straps-sleeves/long-sleeves',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY SILHOUETTE',
                    children: [
                      {
                        label: 'Mermaid',
                        href: '/wedding-dresses/silhouette/mermaid',
                      },
                      {
                        label: 'A Line',
                        href: '/wedding-dresses/silhouette/a-line',
                      },
                      {
                        label: 'Ball Gown',
                        href: '/wedding-dresses/silhouette/ball-gown',
                      },
                      {
                        label: 'Sheath',
                        href: '/wedding-dresses/silhouette/sheath',
                      },
                    ],
                  },
                  websiteEnv.isUnitedStates
                    ? {
                        label: 'ACCESSORIES',
                        children: [
                          {
                            label: 'Shoes',
                            href: '/wedding-shoes',
                          },
                          {
                            label: 'Bridal Robes',
                            href: '/bridal-robes',
                          },
                          {
                            label: 'Groomsmen',
                            href: '/groom-gifts',
                          },
                        ],
                      }
                    : null,
                ]
                  .filter(x => !!x)
                  .map(({ label, children }, i) => {
                    return (
                      <li
                        key={i}
                        className='flex-none'
                      >
                        <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                        <ul className='mt-5 flex flex-col gap-y-1.5'>
                          {children.map(({ label, href }, i) => {
                            return (
                              <li key={i}>
                                <HoverPrefetchLink
                                  href={href}
                                  className='text-[15px] leading-[21px] lg_hover_underline'
                                >
                                  {label}
                                </HoverPrefetchLink>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
              </ul>

              <div className=' flex-none'>
                <HoverPrefetchLink
                  href='/boho-wedding-dresses'
                  className='peer block'
                >
                  <Image
                    src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/brides-boho-wedding-dresses.png`}
                    width={463}
                    height={320}
                    alt='boho-wedding-dresses'
                    quality={90}
                  />
                </HoverPrefetchLink>
                <HoverPrefetchLink
                  href='/boho-wedding-dresses'
                  className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                >
                  BOHO WEDDING DRESSES
                </HoverPrefetchLink>
              </div>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'GUEST',
        href: '/wedding-guest-dresses',
        visible: true,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex  items-start justify-start gap-x-[100px]'>
                {[
                  {
                    label: 'WEDDING GUEST DRESSES',
                    children: [
                      {
                        label: 'All Wedding Guest Dresses',
                        href: '/wedding-guest-dresses',
                      },
                      {
                        label: 'Best Seller',
                        href: '/wedding-guest-dresses?sort_by=best_sellers',
                      },
                      {
                        label: 'Black Tie Wedding Guest',
                        href: '/black-tie-wedding-guest-dresses',
                      },
                      {
                        label: 'Spring Wedding Guest',
                        href: '/spring-wedding-guest-dresses',
                      },
                      {
                        label: 'Plus Size Wedding Guest',
                        href: '/plus-size-wedding-guest-dresses',
                      },
                    ],
                  },
                  {
                    label: 'DRESS CODE',
                    children: [
                      {
                        label: 'Casual',
                        href: '/casual-wedding-guest-dresses',
                      },
                      {
                        label: 'Cocktail',
                        href: '/cocktail-wedding-guest-dresses',
                      },
                      {
                        label: 'Beach',
                        href: '/beach-wedding-guest-dresses',
                      },
                      {
                        label: 'Garden',
                        href: '/garden-wedding-guest-dresses',
                      },
                      {
                        label: 'Black Tie',
                        href: '/black-tie-wedding-guest-dresses',
                      },
                      {
                        label: 'Country',
                        href: '/country-wedding-guest-dresses',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY SEASON',
                    children: [
                      {
                        label: 'Spring',
                        href: '/spring-wedding-guest-dresses',
                      },
                      {
                        label: 'Summer',
                        href: '/summer-wedding-guest-dresses',
                      },
                      {
                        label: websiteEnv.isUnitedKingdom || websiteEnv.isAustralia ? 'Autumn' : 'Fall',
                        href:
                          websiteEnv.isUnitedKingdom || websiteEnv.isAustralia
                            ? '/autumn-wedding-guest-dresses'
                            : '/fall-wedding-guest-dresses',
                      },
                      {
                        label: 'Winter',
                        href: '/winter-wedding-guest-dresses',
                      },
                    ],
                  },
                ].reduce<ReactNode[]>((acc, { label, children }, i, arr) => {
                  acc.push(
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>,
                  );

                  if (i === 0 && guestColorMenu) {
                    acc.push(<Fragment key='wedding-guest-dresses'>{guestColorMenu}</Fragment>);
                  }

                  return acc;
                }, [])}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/guest-black-tie-wedding-guest.png`,
                    label: 'BLACK TIE WEDDING GUEST',
                    href: '/black-tie-wedding-guest-dresses',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/guest-casual-wedding-guest.png`,
                    label: 'CASUAL WEDDING GUEST DRESSES',
                    href: '/casual-wedding-guest-dresses',
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={220}
                          height={320}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'MOMS',
        href: '/mother-of-the-bride-dresses',
        visible: !isAttire,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex  items-start justify-start gap-x-[100px]'>
                {[
                  {
                    label: 'MOTHER OF THE BRIDE DRESSES',
                    children: [
                      {
                        label: 'All Mother of the Bride Dresses',
                        href: '/mother-of-the-bride-dresses',
                      },
                      {
                        label: 'New Arrivals',
                        href: '/mother-of-the-bride-dresses?sort_by=new',
                      },
                      {
                        label: (
                          <span>
                            <span>Under</span>
                            &nbsp;
                            <Price
                              value={100}
                              showFractionDigits={false}
                              currencyCode={process.env.NEXT_PUBLIC_DEFAULT_DISPLAY_CURRENCY_CODE}
                            />
                          </span>
                        ),
                        href: '/mother-of-the-bride-dresses/price/0-100',
                      },
                      {
                        label: 'Spring Mother of the Bride Dresses',
                        href: '/spring-mother-of-the-bride-dresses',
                      },
                      {
                        label: 'Jumpsuits',
                        href: '/mother-of-the-bride-dresses/silhouette/jumpsuit',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY STYLE',
                    children: [
                      {
                        label: 'With Wraps',
                        href: '/mother-of-the-bride-dresses-with-wraps',
                      },
                      {
                        label: 'A-Line',
                        href: '/mother-of-the-bride-dresses/silhouette/a-line',
                      },
                      {
                        label: 'Lace',
                        href: `/mother-of-the-bride-dresses/fabric/lace`,
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY LENGTH',
                    children: [
                      {
                        label: 'Knee',
                        href: '/mother-of-the-bride-dresses/hemline/knee-length',
                      },
                      {
                        label: 'Tea',
                        href: '/mother-of-the-bride-dresses/hemline/tea-length',
                      },
                      {
                        label: 'Ankle',
                        href: '/mother-of-the-bride-dresses/hemline/ankle-length',
                      },
                    ],
                  },
                ].reduce<ReactNode[]>((acc, { label, children }, i, arr) => {
                  acc.push(
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='block max-w-max text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>,
                  );

                  if (i === 0 && momsColorMenu) {
                    acc.push(<Fragment key='mother-of-the-bride-dresses'>{momsColorMenu}</Fragment>);
                  }

                  return acc;
                }, [])}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/mother-of-the-bride-dresses.png`,
                    label: 'MOTHER OF THE BIRDE DRESSES',
                    href: '/mother-of-the-bride-dresses',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/moms-jumpsuits.png`,
                    label: 'JUMPSUITS',
                    href: '/mother-of-the-bride-dresses/silhouette/jumpsuit',
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={220}
                          height={320}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'DRESSES',
        href: '/dresses',
        visible: !isAttire,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex  items-start justify-start gap-x-[57px]'>
                {[
                  {
                    label: 'FEATURED',
                    children: [
                      {
                        label: 'All Dresses',
                        href: '/dresses',
                      },
                      {
                        label: 'Floral Dresses',
                        href: '/floral-dresses',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY OCCASION',
                    children: [
                      {
                        label: 'Prom Dresses',
                        href: '/prom-dresses',
                      },
                      {
                        label: 'Homecoming Dresses',
                        href: '/homecoming-dresses',
                      },
                      {
                        label: 'Graduation Dresses',
                        href: '/graduation-dresses',
                      },
                      {
                        label: 'Formal Dresses',
                        href: '/formal-dresses',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY LENGTH',
                    children: [
                      {
                        label: 'Mini Dress',
                        href: '/dresses/hemline/knee-length',
                      },
                      {
                        label: 'Midi Dress',
                        href: '/dresses/hemline/tea-length',
                      },
                      {
                        label: 'Maxi Dress',
                        href: '/dresses/hemline/floor-length',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY FABRIC',
                    children: [
                      {
                        label: 'Satin Charmeuse',
                        href: '/dresses/fabric/satin-charmeuse',
                      },
                      {
                        label: 'Velvet',
                        href: '/dresses/fabric/velvet',
                      },
                      {
                        label: 'Tulle',
                        href: '/dresses/fabric/tulle',
                      },
                      {
                        label: 'Composite Filament',
                        href: '/dresses/fabric/composite-filament',
                      },
                    ],
                  },
                ].reduce<ReactNode[]>((acc, { label, children }, i, arr) => {
                  acc.push(
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>,
                  );

                  if (i === 2 && dressesColorMenu) {
                    acc.push(<Fragment key='dresses'>{dressesColorMenu}</Fragment>);
                  }

                  return acc;
                }, [])}
              </ul>

              <div className=' flex-none'>
                <HoverPrefetchLink
                  href='/floral-dresses'
                  className='peer block'
                >
                  <Image
                    src={`${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/dresses-best-seller.png`}
                    width={463}
                    height={320}
                    alt='best-seller'
                    quality={90}
                  />
                </HoverPrefetchLink>
                <HoverPrefetchLink
                  href='/floral-dresses'
                  className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                >
                  BEST SELLER
                </HoverPrefetchLink>
              </div>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'HOME TRY ON',
        href: '/home-try-on',
        visible: websiteEnv.isUnitedStates && !isAttire,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul>
                {[
                  {
                    label: 'HOME TRY ON DRESSES',
                    children: [
                      {
                        label: 'All Dresses',
                        href: '/sample-bridesmaids',
                      },
                      {
                        label: 'How It Works',
                        href: '/home-try-on',
                      },
                      {
                        label: 'Bridesmaid Dresses',
                        href: '/sample-bridesmaids',
                      },
                      {
                        label: 'Mother of the Bride Dresses',
                        href: '/sample-mother-of-the-bride',
                      },
                    ],
                  },
                ].map(({ label, children }, i) => {
                  return (
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/home-try-on-bridemaid-dresses.png`,
                    label: 'BRIDESMAID DRESSES',
                    href: '/sample-bridesmaids',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/home-try-on-mother-of-the-bride-dresses.png`,
                    label: 'MOTHER OF THE BRIDE DRESSES',
                    href: '/sample-mother-of-the-bride',
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={463}
                          height={320}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'FABRIC & SWATCHES',
        href: '/swatches',
        visible: !isAttire,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul>
                {[
                  {
                    label: 'FABRIC & SWATCHES',
                    children: [
                      {
                        label: '(Free!) Swatches',
                        href: '/swatches',
                      },
                      {
                        label: 'Fabric By Yard',
                        href: '/fabrics',
                      },
                    ],
                  },
                ].map(({ label, children }, i) => {
                  return (
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/fabric-swatches-velvet.png`,
                    label: 'SATIN CHARMEUSE',
                    href: '/swatches/fabric/satin-charmeuse',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/fabric-swatches-satin-charmeuse.png`,
                    label: 'VELVET',
                    href: '/swatches/fabric/velvet',
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={463}
                          height={320}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'SHOES',
        href: '/wedding-shoes',
        visible: websiteEnv.isUnitedStates,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex items-start gap-x-[100px]'>
                {[
                  {
                    label: 'WEDDING SHOES',
                    children: [
                      {
                        label: 'All Wedding Shoes',
                        href: '/wedding-shoes',
                      },
                      {
                        label: 'New Arrivals',
                        href: '/wedding-shoes?sort_by=new',
                      },
                      {
                        label: 'Best Seller',
                        href: '/wedding-shoes?sort_by=best_sellers',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY STYLE',
                    children: [
                      {
                        label: 'Heels',
                        href: '/wedding-shoes/heel-height/high-heel',
                      },
                      {
                        label: 'Flats',
                        href: '/wedding-shoes/heel-height/flat',
                      },
                    ],
                  },
                  {
                    label: websiteEnv.isUnitedKingdom ? 'SHOP BY COLOUR' : 'SHOP BY COLOR',
                    children: [
                      {
                        label: 'Apricot',
                        href: '/wedding-shoes/color/apricot',
                      },
                      {
                        label: 'White',
                        href: '/wedding-shoes/color/white',
                      },
                      {
                        label: 'Gold',
                        href: '/wedding-shoes/color/gold',
                      },
                      {
                        label: 'Silver',
                        href: '/wedding-shoes/color/silver',
                      },
                      {
                        label: 'Blush',
                        href: '/wedding-shoes/color/blush',
                      },
                    ],
                  },
                ].map(({ label, children }, i) => {
                  return (
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shoes-wedding-shoes.png`,
                    label: 'WEDDING SHOES',
                    href: '/wedding-shoes',
                    width: 463,
                    height: 320,
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shoes-best-seller.png`,
                    label: 'BEST SELLER',
                    href: '/wedding-shoes?sort_by=best_sellers',
                    width: 320,
                    height: 320,
                  },
                ].map(({ src, label, href, width, height }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={width}
                          height={height}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'ACCESSORIES',
        href: '/wedding-accessories-list',
        visible: websiteEnv.isUnitedStates,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex items-start gap-x-[76px]'>
                {[
                  {
                    label: 'FEATURED',
                    children: [
                      {
                        label: 'All Accessories',
                        href: '/wedding-accessories-list',
                      },
                      {
                        label: 'Jackets & Wraps',
                        href: '/wedding-jackets-wraps',
                      },
                      {
                        label: 'Tiaras',
                        href: '/bridal-tiaras',
                      },
                    ],
                  },
                  {
                    label: 'ROBES',
                    children: [
                      {
                        label: 'All Robes',
                        href: '/wedding-bathrobes',
                      },
                      {
                        label: 'Bridal & Bridesmaid Robes',
                        href: '/bridal-bridesmaid-robes',
                      },
                      {
                        label: 'Couple Robes',
                        href: '/couple-robes',
                      },
                      {
                        label: 'Pajamas',
                        href: '/pajamas',
                      },
                    ],
                  },
                ].map(({ label, children }, i) => {
                  return (
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/accessories-jackets-wraps.png`,
                    label: 'JACKETS & WRAPS',
                    href: '/wedding-jackets-wraps',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/accessories-couple-robes.png`,
                    label: 'COUPLE ROBES',
                    href: '/couple-robes',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/accessories-pajamas.png`,
                    label: 'PAJAMAS',
                    href: '/pajamas',
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={220}
                          height={320}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'GIFTS & DECOR',
        href: '/wedding-gifts-decor',
        visible: websiteEnv.isUnitedStates && !isAttire,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex items-start gap-x-[76px]'>
                {[
                  {
                    label: 'SHOP BY TYPE',
                    children: [
                      {
                        label: 'All Gifts & Decor',
                        href: '/wedding-gifts-decor',
                      },
                      {
                        label: 'For Bride',
                        href: '/bride-gifts',
                      },
                      {
                        label: 'For Bridesmaid',
                        href: '/bridesmaid-gifts',
                      },
                      {
                        label: 'For Groom',
                        href: '/groom-gifts',
                      },
                      {
                        label: 'For Couple',
                        href: '/couple-gifts',
                      },
                    ],
                  },
                  {
                    label: 'SHOP BY CATEGORY',
                    children: [
                      {
                        label: 'Ring Box',
                        href: '/ring-box',
                      },
                      {
                        label: 'Cake Knife',
                        href: '/wedding-cake-knife',
                      },
                      {
                        label: 'Bags & Cases',
                        href: '/bags-cases',
                      },
                      {
                        label: 'Guest Book',
                        href: '/guest-book',
                      },
                      {
                        label: 'Gift Boxes',
                        href: '/gift-boxes',
                      },
                      {
                        label: 'Shadow Box',
                        href: '/shaow-box',
                      },
                      {
                        label: 'Glassware & Drinkware',
                        href: '/glassware-drinkware',
                      },
                    ],
                  },
                ].map(({ label, children }, i) => {
                  return (
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/gifts-decor-cake-knife.png`,
                    label: 'CAKE KNIFE',
                    href: '/wedding-cake-knife',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/gifts-decor-gift-boxes.png`,
                    label: 'GIFT BOXES',
                    href: '/gift-boxes',
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={463}
                          height={320}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
      {
        label: 'SALE',
        href: '/wedding-sales',
        visible: !isAttire,
        dropdown: (
          <DropdownWrapper>
            <div className='flex items-start justify-between'>
              <ul className='flex items-start gap-x-[76px]'>
                {[
                  {
                    label: 'SALE',
                    children: [
                      {
                        label: 'All Sale',
                        href: '/wedding-sales',
                      },
                      {
                        label: 'Sale Bridesmaid Dresses',
                        href: '/bridesmaid-dresses-sale',
                      },
                      {
                        label: 'Sale Wedding Dresses',
                        href: '/wedding-dresses-sale',
                      },
                    ],
                  },
                ].map(({ label, children }, i) => {
                  return (
                    <li
                      key={i}
                      className='flex-none'
                    >
                      <span className='text-[15px] font-medium leading-[21px]'>{label}</span>

                      <ul className='mt-5 flex flex-col gap-y-1.5'>
                        {children.map(({ label, href }, i) => {
                          return (
                            <li key={i}>
                              <HoverPrefetchLink
                                href={href}
                                className='text-[15px] leading-[21px] lg_hover_underline'
                              >
                                {label}
                              </HoverPrefetchLink>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              <ul className=' flex flex-none items-center gap-x-[30px]'>
                {[
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/sale-sale-bridesmaid-dresses.png`,
                    label: 'SALE BRIDESMAID DRESSES',
                    href: '/bridesmaid-dresses-sale',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/sale-sale-dresses.png`,
                    label: 'SALE DRESSES',
                    href: '/wedding-sales',
                  },
                  {
                    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/sale-sale-wedding-dress.png`,
                    label: 'SALE WEDDING DRESSES',
                    href: '/wedding-dresses-sale',
                  },
                ].map(({ src, label, href }, i) => {
                  return (
                    <li key={i}>
                      <HoverPrefetchLink
                        href={href}
                        className='peer block'
                      >
                        <Image
                          src={src}
                          width={320}
                          height={320}
                          alt={label}
                          quality={90}
                        />
                      </HoverPrefetchLink>
                      <HoverPrefetchLink
                        href={href}
                        className='mt-2.5 block max-w-max text-sm leading-5 hover_underline peer-hover_underline'
                      >
                        {label}
                      </HoverPrefetchLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </DropdownWrapper>
        ),
      },
    ];
  }, [colorMenus, isAttire]);

  return (
    <div className={clsx(' hidden flex-wrap justify-center gap-x-10 lg_flex')}>
      {menus.map(({ label, href, dropdown, visible = true }, index) => {
        if (!visible) return null;

        return (
          <MenuTooltip
            key={index}
            label={label}
            href={href}
            dropdown={dropdown}
          />
        );
      })}
    </div>
  );
};

const MenuTooltip = ({ href, label, dropdown }: { href: string; label: ReactNode; dropdown: ReactNode }) => {
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip
        open={open}
        onOpenChange={setOpen}
      >
        <TooltipTrigger asChild>
          <div className={clsx(' group flex items-center')}>
            <HoverPrefetchLink
              className={clsx(
                'text-[15px] leading-[21px] text-#403732',
                'relative flex py-5 ',
                'after_absolute after_bottom-0 after_left-0 after_content-[""] ',
                'after_h-[3px] after_w-0 after_bg-BE9983 after_opacity-0',
                'lg_group-hover_after_w-full lg_group-hover_after_opacity-100',
                'after_transition-[opacity,width] after_duration-xl  after_ease-linear',
                open ? 'lg_after_w-full lg_after_opacity-100' : '',
              )}
              href={href}
            >
              {label}
            </HoverPrefetchLink>
          </div>
        </TooltipTrigger>
        <TooltipContent
          className='relative z-[60] w-[--radix-tooltip-content-available-width]'
          onClick={e => {
            const eventTarget = e.target;

            if (eventTarget instanceof Element && eventTarget.closest('a')) {
              setOpen(false);
            }
          }}
        >
          {dropdown}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const DropdownWrapper = ({ children }: { children?: ReactNode }) => {
  return (
    <div className='bg-white'>
      <div className={clsx('mx-auto  max-w-site  py-10 text-#403732')}>{children}</div>
    </div>
  );
};

const generateColorMenuNode = (menus: ColorOption[], title: ReactNode, categoryUrlKey: string) => {
  return (
    <li className='flex-none'>
      <span className='text-[15px] font-medium leading-[21px]'>{title}</span>

      <ul className='mt-5 flex flex-col gap-y-2'>
        {menus.map((x, i) => {
          if (!x || !x.label || !x.swatch_data) return null;

          const {
            label,
            swatch_data: { thumbnail, value },
          } = x;

          if (!thumbnail || !value) return null;

          const colorsFamilyRewrite = label?.toLowerCase().replace(/\s/g, '-') || '';
          const href = colorsFamilyRewrite
            ? `${categoryUrlKey}/${COLORS_FAMILY_KEY}/${colorsFamilyRewrite}`
            : categoryUrlKey;

          if (colorsFamilyRewrite === 'all') {
            return null;
          }

          return (
            <li key={i}>
              <HoverPrefetchLink
                href={href}
                className='flex items-center gap-x-[7px] text-[15px] leading-[21px] lg_hover_underline'
              >
                <span
                  className={clsx(
                    'block h-5 w-5 rounded-[5px] bg-contain',
                    isWhite(value) ? 'border-1 border-solid border-E7DED5' : '',
                  )}
                  style={createColorStyle(thumbnail, value)}
                />
                <span>{label}</span>
              </HoverPrefetchLink>
            </li>
          );
        })}
      </ul>
    </li>
  );
};

const isWhite = (value: string) => {
  const lowercaseValue = value.toLowerCase();

  return lowercaseValue === '#fff' || lowercaseValue === '#ffffff';
};

const createColorStyle = (thumbnail: string | undefined, value: string | undefined) => {
  if (!thumbnail && !value) {
    return {};
  }

  if (thumbnail && thumbnail.indexOf('#') < 0) {
    const imagePath = generateUrl(thumbnail, 'image-swatch')(48);

    return {
      backgroundImage: `url("${imagePath}")`,
    };
  }

  return {
    backgroundColor: value,
  };
};

export default NavMenu;
